"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { motion } from "framer-motion"
import { Send as SendIcon, Search as SearchIcon } from "@mui/icons-material"
import { API_BASE } from "../config/api"

const QueryContainer = styled(motion.div)`
  background-color: var(--input-bg);
  border-radius: 8px;
  padding: 30px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  border: 1px solid var(--border);
`

const QueryTitle = styled.h2`
  margin-bottom: 24px;
  color: var(--primary-text);
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: 10px;
`

const QueryDescription = styled.p`
  color: var(--secondary-text);
  margin-bottom: 24px;
  line-height: 1.6;
`

const QueryForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const InputWrapper = styled.div`
  position: relative;
`

const QueryTextarea = styled(motion.textarea)`
  width: 100%;
  padding: 16px;
  background-color: rgba(13, 12, 29, 0.6);
  border: 1px solid var(--border);
  border-radius: 4px;
  color: var(--primary-text);
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 2px rgba(79, 141, 251, 0.3);
  }
  
  &::placeholder {
    color: rgba(176, 176, 176, 0.5);
  }
`

const SubmitButton = styled(motion.button)`
  align-self: flex-end;
  padding: 12px 24px;
  background-color: var(--accent-blue);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: var(--highlight);
  }
  
  &:disabled {
    background-color: rgba(79, 141, 251, 0.5);
    cursor: not-allowed;
  }
`

const ExampleQueries = styled.div`
  margin-top: 30px;
`

const ExampleTitle = styled.h3`
  color: var(--secondary-text);
  font-size: 1rem;
  margin-bottom: 12px;
`

const ExampleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ExampleQuery = styled(motion.button)`
  background-color: rgba(79, 141, 251, 0.1);
  border: 1px solid rgba(79, 141, 251, 0.3);
  border-radius: 4px;
  padding: 10px 16px;
  color: var(--primary-text);
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(79, 141, 251, 0.2);
    border-color: var(--accent-blue);
  }
`

const LoadingSpinner = styled(motion.div)`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
`

const QueryInput = ({ onSubmit, onProgress, onResult, isLoading }) => {
  const [query, setQuery] = useState("")
  const [eventSource, setEventSource] = useState(null)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (query.trim() && !isLoading) {
      onSubmit(query)
      
      // Use fetch with streaming response
      fetch(`${API_BASE}/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question: query }),
        credentials: 'include',
      })
      .then(response => {
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        function readStream() {
          reader.read().then(({ done, value }) => {
            if (done) {
              return
            }

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            
            // Keep the last line in the buffer if it's incomplete
            buffer = lines.pop() || ''
            
            lines.forEach(line => {
              if (line.trim() && line.startsWith('data: ')) {
                try {
                  const jsonStr = line.slice(6) // Remove 'data: ' prefix
                  const data = JSON.parse(jsonStr)
                  if (data.event === "progress") {
                    const progress = JSON.parse(data.data)
                    onProgress(progress)
                  } else if (data.event === "result") {
                    const result = JSON.parse(data.data)
                    onResult(result)
                  } else if (data.event === "error") {
                    const error = JSON.parse(data.data)
                    onProgress(error)
                  }
                } catch (e) {
                  console.error('Error parsing SSE data:', e)
                }
              }
            })

            readStream()
          })
        }

        readStream()
      })
      .catch(error => {
        onProgress({
          step: "error",
          message: "Connection error. Please try again.",
          data: null,
        })
      })
    }
  }

  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close()
      }
    }
  }, [eventSource])

  const handleExampleClick = (exampleQuery) => {
    setQuery(exampleQuery)
  }

  const exampleQueries = [
    "What are the total pallets shipped per customer in January 2024?",
    "Show me the top 5 customers by total cost",
    "What is the average distance for shipments to each postcode?",
    "Compare total orders and pallets for each product type",
  ]

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  const inputVariants = {
    focus: {
      scale: 1.01,
      boxShadow: "0 0 0 2px rgba(79, 141, 251, 0.3)",
    },
  }

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: {
        yoyo: Number.POSITIVE_INFINITY,
        duration: 0.5,
      },
    },
    tap: { scale: 0.95 },
  }

  const exampleVariants = {
    hover: {
      scale: 1.02,
      x: 5,
    },
    tap: { scale: 0.98 },
  }

  const spinnerVariants = {
    animate: {
      rotate: 360,
      transition: {
        loop: Number.POSITIVE_INFINITY,
        ease: "linear",
        duration: 1,
      },
    },
  }

  return (
    <QueryContainer variants={containerVariants} initial="hidden" animate="visible">
      <QueryTitle>
        <SearchIcon />
        Ask a Question
      </QueryTitle>

      <QueryDescription>
        Ask any question about the logistics data in natural language. The system will generate SQL queries, Python
        code, and visualizations to answer your question.
      </QueryDescription>

      <QueryForm onSubmit={handleSubmit}>
        <InputWrapper>
          <QueryTextarea
            placeholder="e.g., What are the total pallets shipped per customer in January 2024?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            variants={inputVariants}
            whileFocus="focus"
          />
        </InputWrapper>

        <SubmitButton
          type="submit"
          disabled={!query.trim() || isLoading}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
        >
          {isLoading ? (
            <LoadingSpinner variants={spinnerVariants} animate="animate" />
          ) : (
            <>
              <SendIcon fontSize="small" />
              Submit
            </>
          )}
        </SubmitButton>
      </QueryForm>

      <ExampleQueries>
        <ExampleTitle>Example Questions:</ExampleTitle>
        <ExampleList>
          {exampleQueries.map((example, index) => (
            <ExampleQuery
              key={index}
              onClick={() => handleExampleClick(example)}
              variants={exampleVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {example}
            </ExampleQuery>
          ))}
        </ExampleList>
      </ExampleQueries>
    </QueryContainer>
  )
}

export default QueryInput
